import { db, newId, type Item } from './schema';
import { forgetSizes, storeImage } from './images';
import { nextSort } from './sections';
import type { Container } from './board';

/**
 * An item is a cover photo and a title. Everything else — description, notes,
 * price, the gallery — arrives in stages 3 and 4 and is optional forever.
 */
export type ItemDraft = {
  title: string;
  cover: Blob;
};

/** New items land in whatever container you were looking at. */
export async function createItem(container: Container, draft: ItemDraft): Promise<string> {
  return insert(container, draft, await nextSort(container));
}

export type BulkResult = {
  created: string[];
  /** Filenames that couldn't be read. A dropped folder of holiday snaps will
      usually contain one thing that isn't an image; that shouldn't lose the
      other nineteen. */
  failed: string[];
};

/**
 * One item per file, in the order dropped. Sequential rather than
 * `Promise.all`: twenty simultaneous `createImageBitmap` calls on full-size
 * phone photos will stall the frame the drop landed on, and the sort values
 * have to come out in a predictable order anyway.
 */
export async function createItems(
  container: Container,
  drafts: readonly ItemDraft[],
): Promise<BulkResult> {
  let sort = await nextSort(container);
  const result: BulkResult = { created: [], failed: [] };

  for (const draft of drafts) {
    try {
      result.created.push(await insert(container, draft, sort));
      sort += 1;
    } catch {
      // The filename, when there is one: "broken.png couldn't be read" says
      // which file to go and look at, where the stripped title doesn't.
      result.failed.push(draft.cover instanceof File ? draft.cover.name : draft.title);
    }
  }

  return result;
}

/** Everything the panel edits. Images go through `db/gallery.ts` instead. */
export type ItemPatch = Partial<
  Pick<Item, 'title' | 'description' | 'notes' | 'price' | 'currency' | 'sourceUrl'>
>;

/**
 * The panel's one write. Autosave means nobody pressed a button, so a failure
 * has to throw loudly enough for the panel to keep a warning on screen —
 * swallowing it here would lose the user's typing silently.
 */
export async function updateItemFields(id: string, patch: ItemPatch): Promise<void> {
  const updated = await db.items.update(id, patch);
  // Dexie reports 0 both for "no such record" and for a write that changed
  // nothing. Only the first is a failure.
  if (updated === 0 && (await db.items.get(id)) === undefined) {
    throw new Error(`No item ${id}`);
  }
}

/**
 * Bought a lamp for the bedroom, decided it belongs in the office.
 *
 * The item lands at the top of the destination room, not inside whatever
 * section it was in — the sections in one room mean nothing in another, and a
 * thing you just moved should be the first thing you see there.
 *
 * Images aren't touched: they belong to the item, and the item is the same
 * item. Only `roomId` moves.
 */
export async function moveItemsToRoom(
  ids: readonly string[],
  roomId: string,
): Promise<void> {
  // Read before the writes start, then counted up: `nextSort` walks the whole
  // room, and asking it once per item would be a walk per item.
  let sort = await nextSort({ roomId, sectionId: undefined });

  await db.transaction('rw', db.items, async () => {
    for (const id of ids) {
      await db.items.update(id, { roomId, sectionId: undefined, sort });
      sort += 1;
    }
  });
}

/** The item and every image it owns, in one transaction. */
export function deleteItem(id: string): Promise<void> {
  return deleteItems([id]);
}

/** The same, for a selection. One transaction across the whole lot. */
export async function deleteItems(ids: readonly string[]): Promise<void> {
  const gone = await db.transaction('rw', db.items, db.images, async () => {
    const items = (await db.items.bulkGet([...ids])).filter((item) => item !== undefined);

    const imageIds = items.flatMap((item) => [
      ...(item.coverImageId === undefined ? [] : [item.coverImageId]),
      ...item.imageIds,
    ]);

    await db.images.bulkDelete(imageIds);
    await db.items.bulkDelete(items.map((item) => item.id));
    return imageIds;
  });

  // Outside the transaction: it's a memory cache, and dropping it early would
  // be wrong if the write rolled back.
  forgetSizes(gone);
}

/** "sofa-linen-2.jpg" → "sofa-linen-2". Extension only; the rest is the user's. */
export function titleFromFilename(name: string): string {
  const stripped = name.replace(/\.[^./\\]+$/, '').trim();
  return stripped === '' ? 'Untitled' : stripped;
}

async function insert(
  container: Container,
  draft: ItemDraft,
  sort: number,
): Promise<string> {
  // Outside any transaction: canvas encoding is slow enough to time one out.
  const coverImageId = await storeImage(draft.cover);

  const item: Item = {
    id: newId(),
    roomId: container.roomId,
    sectionId: container.sectionId,
    title: draft.title.trim() === '' ? 'Untitled' : draft.title.trim(),
    description: '',
    notes: '',
    coverImageId,
    imageIds: [],
    sort,
    createdAt: Date.now(),
  };

  await db.items.add(item);
  return item.id;
}
